import { memo, useState, useCallback } from 'react';
import {
  Button,
  Input,
  Label,
  Separator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
} from '@flote/ui';
import { Loader2, User, Phone, Car, Users } from 'lucide-react';
import type { Team, CreateDriverRequest, Vehicle } from '@flote/contracts/http';

export type DriverFormData = CreateDriverRequest;

interface DriverFormProps {
  onSubmit: (data: DriverFormData) => Promise<void>;
  isProcessing?: boolean;
  teams: Team[];
  vehicles: Vehicle[];
  initialData?: Partial<DriverFormData>;
  mode?: 'create' | 'edit';
}

export const DriverForm = memo<DriverFormProps>(({
  onSubmit,
  isProcessing = false,
  teams,
  vehicles,
  initialData,
  mode = 'create',
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [name, setName] = useState(initialData?.name || '');
  const [phone, setPhone] = useState(initialData?.phone || '');
  const [displayName, setDisplayName] = useState(initialData?.displayName || '');
  const [selectedTeams, setSelectedTeams] = useState<string[]>(initialData?.teams || []);
  const [capacity, setCapacity] = useState<string>(initialData?.capacity?.toString() || '');

  // Vehicle selection (now using existing vehicles)
  const [selectedVehicleId, setSelectedVehicleId] = useState(initialData?.vehicleId || 'none');

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Name and phone are only required in create mode (can't be updated in edit mode)
    if (mode === 'create') {
      if (!name.trim()) {
        newErrors.name = 'Driver name is required';
      }

      if (!phone.trim()) {
        newErrors.phone = 'Phone number is required';
      }
    }

    if (selectedTeams.length === 0) {
      newErrors.teams = 'At least one team is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildDriverRequest = (): DriverFormData => {
    const request: DriverFormData = {
      name: name.trim(),
      phone: phone.trim(),
      teams: selectedTeams,
    };

    if (displayName.trim()) {
      request.displayName = displayName.trim();
    }

    if (capacity) {
      const capacityNum = parseInt(capacity, 10);
      if (!isNaN(capacityNum) && capacityNum > 0) {
        request.capacity = capacityNum;
      }
    }

    if (mode !== 'create') {
      request.vehicleId = selectedVehicleId === 'none' ? null : selectedVehicleId;
    } else if (selectedVehicleId && selectedVehicleId !== 'none') {
      request.vehicleId = selectedVehicleId;
    }

    return request;
  };

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const driverRequest = buildDriverRequest();
      await onSubmit(driverRequest);
    } finally {
      setIsSubmitting(false);
    }
  }, [name, phone, displayName, selectedTeams, capacity, selectedVehicleId, onSubmit]);

  const handleTeamToggle = (teamId: string) => {
    setSelectedTeams(prev =>
      prev.includes(teamId)
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const processing = isSubmitting || isProcessing;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Driver Information Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <User className="h-4 w-4 text-muted-foreground" />
          Driver Information
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Name {mode === 'create' ? '*' : ''}</Label>
          <Input
            id="name"
            placeholder="e.g., John Smith"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={processing || mode === 'edit'}
            className={errors.name ? 'border-destructive' : mode === 'edit' ? 'bg-muted' : ''}
          />
          {errors.name && (
            <p className="text-xs text-destructive">{errors.name}</p>
          )}
          {mode === 'edit' && (
            <p className="text-xs text-muted-foreground">
              Name cannot be changed after creation
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="displayName">Display Name</Label>
          <Input
            id="displayName"
            placeholder="e.g., Johnny (optional)"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={processing}
          />
          <p className="text-xs text-muted-foreground">
            Shown to customers instead of full name
          </p>
        </div>
      </div>

      <Separator />

      {/* Contact Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Phone className="h-4 w-4 text-muted-foreground" />
          Contact
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number {mode === 'create' ? '*' : ''}</Label>
          <Input
            id="phone"
            placeholder="e.g., +15551234567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={processing || mode === 'edit'}
            className={errors.phone ? 'border-destructive' : mode === 'edit' ? 'bg-muted' : ''}
          />
          {errors.phone && (
            <p className="text-xs text-destructive">{errors.phone}</p>
          )}
          {mode === 'create' ? (
            <p className="text-xs text-muted-foreground">
              Include country code (e.g., +1 for US)
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Phone number cannot be changed after creation
            </p>
          )}
        </div>
      </div>

      <Separator />

      {/* Team Assignment Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Users className="h-4 w-4 text-muted-foreground" />
          Team Assignment *
        </div>

        {teams.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No teams available. Create a team first.
          </p>
        ) : (
          <div className="space-y-2">
            <div className="grid gap-2">
              {teams.map((team) => (
                <div key={team.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`team-${team.id}`}
                    checked={selectedTeams.includes(team.id)}
                    onCheckedChange={() => handleTeamToggle(team.id)}
                    disabled={processing}
                  />
                  <Label
                    htmlFor={`team-${team.id}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {team.name}
                  </Label>
                </div>
              ))}
            </div>
            {errors.teams && (
              <p className="text-xs text-destructive">{errors.teams}</p>
            )}
          </div>
        )}
      </div>

      <Separator />

      {/* Vehicle Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Car className="h-4 w-4 text-muted-foreground" />
          Vehicle
          <span className="text-xs font-normal text-muted-foreground">(optional)</span>
        </div>

        <div className="space-y-2">
          <Label htmlFor="vehicle">Assign Vehicle</Label>
          <Select value={selectedVehicleId} onValueChange={setSelectedVehicleId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a vehicle (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No vehicle assigned</SelectItem>
              {vehicles.filter(v => v.status === 'available' || (selectedVehicleId !== 'none' && v.id === selectedVehicleId)).map((vehicle) => (
                <SelectItem key={vehicle.id} value={vehicle.id}>
                  {vehicle.description || vehicle.type}
                  {vehicle.licensePlate && ` (${vehicle.licensePlate})`}
                  {vehicle.status !== 'available' && ' - Currently Assigned'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Select from available fleet vehicles. Manage vehicles in the Vehicles section.
          </p>
        </div>
      </div>

      <Separator />

      {/* Capacity Section */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="capacity">Delivery Capacity</Label>
          <Input
            id="capacity"
            type="number"
            min="1"
            placeholder="e.g., 20"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            disabled={processing}
          />
          <p className="text-xs text-muted-foreground">
            Maximum number of tasks this driver can handle at once
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex pt-2">
        <Button type="submit" disabled={processing || teams.length === 0} className="w-full">
          {processing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {mode === 'create' ? 'Creating Driver...' : 'Updating Driver...'}
            </>
          ) : (
            mode === 'create' ? 'Create Driver' : 'Update Driver'
          )}
        </Button>
      </div>
    </form>
  );
});

DriverForm.displayName = 'DriverForm';
